import { motion } from 'framer-motion';
import type { ProjectSpecs, PublicShowcase } from '../lib/api';
import { specValue } from '../lib/api';

/**
 * Ficha técnica — the numbers + construction details a premium buyer
 * actually wants to see. Two tiers:
 *
 *   1. "Números" — the five headline figures (area, quartos, banheiros,
 *      vagas, lote if present). Large display type, stagger-reveal.
 *   2. "Arquitetura & instalações" — the rest of the spec sheet
 *      (pé-direito, sistema construtivo, acabamentos, automação,
 *      sustentabilidade, segurança) as a two-column key-value list.
 *
 * Extended specs are hardcoded until the Phase 3 admin UI ships so the
 * PM can edit them without a deploy. Shape matches what we'll lift
 * into the `showcases.extended_specs` JSON field in that migration.
 */

interface SpecRow {
  label: string;
  value: string;
}

/**
 * Gênese-specific extended specs. When Phase 3 ships, this list moves
 * to the admin surface so other houses fill in their own.
 */
/**
 * Source of truth: Memorial Descritivo de Obra + Planta Pavimento Térreo
 * + Planta de Cobertura + Corte BB + Fachadas (DestArq Arquitetura,
 * projeto para Jeferson Luís Propheta, Ninho Verde I · Quadra BK ·
 * Lotes 09-10 · Rua 25 · Porangaba-SP).
 *
 * Every number below comes from those documents. When new finishes are
 * specified (piso final, granito, cor da pintura, etc.) we fold them in
 * here — this list is the canonical ficha técnica until the Vitrine
 * Phase 3+ migration lifts it into `showcases.extended_specs` JSON.
 */
/**
 * Schema for the ficha técnica. Each row optionally points at a
 * `project_specs` (namespace, key) tuple. At render time the value is
 * resolved with `specValue(specs, ns, key, fallback)` — if the DB has
 * the row, it wins; otherwise the curated `fallback` text shows.
 *
 * Wave 44.13 — JP can edit any value live in the admin UI. The
 * fallbacks below are the original Wave 44.6 hardcoded strings so the
 * site never goes blank if the DB call fails.
 *
 * Composers (the longer rows) compose multiple spec keys into a single
 * descriptive sentence — they take a `compose(specs)` callback that
 * returns the rendered string, with any missing key falling back
 * gracefully.
 */
type SpecRowDef =
  | { label: string; ns: string; key: string; fallback: string }
  | { label: string; compose: (s: ProjectSpecs) => string };

const SPEC_GROUPS: Array<{ group: string; rows: SpecRowDef[] }> = [
  {
    group: 'Implantação',
    rows: [
      {
        label: 'Endereço',
        compose: (s) =>
          [
            specValue(s, 'identity', 'loteamento', 'Ninho Verde I'),
            specValue(s, 'identity', 'quadra', 'Quadra BK'),
            specValue(s, 'identity', 'lotes', 'Lotes 09-10'),
            specValue(s, 'identity', 'rua', 'Rua 25'),
            specValue(s, 'identity', 'municipio', 'Porangaba / SP'),
          ].join(' · '),
      },
      {
        label: 'Terreno',
        compose: (s) => {
          const real = specValue(s, 'site', 'lot_total_real', '876,05');
          const escr = specValue(s, 'site', 'lot_total_escritura', '900');
          const front = specValue(s, 'site', 'testada', '29,28');
          return `${real} m² de área real (${escr} m² escritura) · ${front} m de testada`;
        },
      },
      {
        label: 'Construída',
        compose: (s) => {
          const total = specValue(s, 'dimensions', 'area_construida_total', '310,60');
          const main = specValue(s, 'dimensions', 'area_corpo_principal', '278,48');
          const gar = specValue(s, 'dimensions', 'area_garagem', '32,12');
          return `${total} m² — corpo principal ${main} + garagem ${gar}`;
        },
      },
      {
        label: 'Taxa de ocupação',
        compose: (s) => {
          const to = specValue(s, 'site', 'taxa_ocupacao', '35,45');
          const ca = specValue(s, 'site', 'coef_aproveitamento', '0,3646');
          const verde = specValue(s, 'site', 'area_verde_permeavel', '37,30');
          return `TO = ${to}% · CA = ${ca} · verde permeável ${verde}%`;
        },
      },
    ],
  },
  {
    group: 'Arquitetura',
    rows: [
      { label: 'Volume',         ns: '_synthetic', key: 'volume', fallback: 'Térreo em L · spine frontal de 29,28 m + ala das suítes ao norte' },
      {
        label: 'Pé-direito',
        compose: (s) => {
          const social = specValue(s, 'heights', 'social_pe_direito', '2,90');
          const garag = specValue(s, 'heights', 'garagem_pe_direito', '2,95');
          const circ = specValue(s, 'heights', 'circulacao_pe_direito', '2,50');
          return `${social} m social · 2,70 m suítes · ${garag} m garagem · ${circ} m circulação`;
        },
      },
      { label: 'Estrutura',      ns: 'structure', key: 'colunas_vigas',  fallback: 'Colunas e vigas de concreto armado · alvenaria em blocos cerâmicos ou concreto' },
      { label: 'Cobertura',      ns: 'roof',      key: 'telhamento',     fallback: 'Telhas metálicas sanduíche 40 mm · caimento 5% aos ralos · platibanda com rufo metálico' },
      { label: 'Fachada frontal',ns: '_synthetic',key: 'facade_summary', fallback: 'Cobogó de concreto · ripado com porta mimetizada · brise telado · pedras moledo' },
      { label: 'Esquadrias',     ns: 'openings',  key: 'esquadrias_material', fallback: 'Alumínio com vidros · porta pivotante no hall · brise vazado de alumínio ou madeira ecológica' },
      { label: 'Domus',          ns: '_synthetic',key: 'domus_summary',  fallback: 'Iluminação zenital sobre o estar e circulação das suítes' },
    ],
  },
  {
    group: 'Ambientes',
    rows: [
      {
        label: 'Social',
        compose: (s) => {
          const cz = specValue(s, 'rooms', 'cozinha_area', '30,26');
          const jt = specValue(s, 'rooms', 'jantar_area',  '16,64');
          const es = specValue(s, 'rooms', 'estar_area',   '48,23');
          return `Cozinha ${cz} · Jantar ${jt} · Estar ${es} m²`;
        },
      },
      { label: 'Gourmet',  ns: 'rooms', key: 'gourmet_area', fallback: '37,50 m² · deck de madeira · churrasqueira com duto metálico preto' },
      {
        label: 'Suítes',
        compose: (s) => {
          const std = specValue(s, 'rooms', 'suite_1_area', '12,30');
          const master = specValue(s, 'rooms', 'suite_4_area', '16,34');
          return `4 suítes — três de ${std} m² + master de ${master} m² · cada uma com banho próprio`;
        },
      },
      {
        label: 'Banhos',
        compose: (s) => {
          const std = specValue(s, 'rooms', 'banho_1_area', '3,12');
          const mst = specValue(s, 'rooms', 'banho_master_area', '4,32');
          return `${std} m² padrão · ${mst} m² master · cerâmica ao teto`;
        },
      },
      {
        label: 'Serviço',
        compose: (s) => {
          const lv = specValue(s, 'rooms', 'lavabo_1_area', '1,95');
          const sv = specValue(s, 'rooms', 'a_serv_area', '3,00');
          const dp = specValue(s, 'rooms', 'despensa_area', '1,95');
          const ac = specValue(s, 'rooms', 'antecamara_area', '1,80');
          return `Lavabo ${lv} · A.Serv ${sv} · Despensa ${dp} · A.Câmara ${ac} m²`;
        },
      },
      { label: 'Garagem', ns: 'rooms', key: 'garagem_area', fallback: '52,68 m² · 2 vagas cobertas + 1 vaga sob pergolado descoberto · acesso em rampa 16%' },
    ],
  },
  {
    group: 'Acabamentos',
    rows: [
      {
        label: 'Pisos',
        compose: (s) => {
          const social = specValue(s, 'finishes', 'piso_social', 'Concreto polido');
          const suites = specValue(s, 'finishes', 'piso_suites', 'Vinílico madeira');
          const banh = specValue(s, 'finishes', 'piso_banhos', 'Cerâmica');
          return `${social} no social + gourmet · ${suites} nas suítes · ${banh} nos banhos, lavabos e varanda`;
        },
      },
      { label: 'Paredes molhadas', ns: 'finishes', key: 'paredes_molhadas', fallback: 'Revestimento impermeabilizante até o teto em banhos, cozinha e lavanderia' },
      { label: 'Paredes secas',    ns: 'finishes', key: 'paredes_secas',    fallback: 'Pintura sobre massa grossa e fina' },
      { label: 'Tetos',            ns: 'finishes', key: 'tetos',            fallback: 'Laje pré-moldada com pintura · gesso acartonado ou lambril de madeira onde houver forro' },
    ],
  },
  {
    group: 'Instalações',
    rows: [
      { label: 'Elétrica',     ns: 'systems', key: 'eletrica',     fallback: 'Embutida · eletrodutos de PVC · fiação anti-chama · disjuntores eletrostáticos' },
      { label: 'Hidráulica',   ns: 'systems', key: 'hidraulica',   fallback: 'Cavalete → reservatório sem tomada intermediária · saída pressurizada' },
      { label: 'Reservatório', ns: 'systems', key: 'reservatorio', fallback: '4 caixas Fortlev de 1.000 L cada (4.000 L total) sobre a ala das suítes' },
      { label: 'Esgoto',       ns: 'systems', key: 'esgoto',       fallback: 'Fossa séptica + filtro anaeróbio + sumidouro (NBR 7229/93 e 13969/97)' },
      { label: 'Gás',          ns: 'systems', key: 'gas',          fallback: 'Botijão em abrigo externo · duto lareira metálico preto' },
    ],
  },
  {
    group: 'Água e exterior',
    rows: [
      {
        label: 'Piscina',
        compose: (s) => {
          const psc = specValue(s, 'exterior', 'piscina_dim', '7,80 × 4,00 m');
          const prn = specValue(s, 'exterior', 'prainha_dim', '2,20 × 4,00 m');
          return `40,00 m² total — piscina ${psc} (h=1,40) + prainha ${prn} (h=0,40) · revestimento cerâmico`;
        },
      },
      { label: 'Solarium',    ns: '_synthetic', key: 'solarium_summary', fallback: 'Deck de madeira · dormente ducha H=2,10 m' },
      { label: 'Paisagismo',  ns: 'exterior',   key: 'paisagismo',       fallback: 'Plantas adultas + dois pontos de iluminação com sensor de luminosidade automático' },
      { label: 'Fechamentos', ns: '_synthetic', key: 'fences_summary',   fallback: 'Cerca viva 1,20 m no recuo frontal · 1,80 m nas demais divisas · testada sem fechamento' },
    ],
  },
  {
    group: 'Automação e segurança',
    rows: [
      { label: 'Monitoramento', ns: 'condominium', key: 'obra_alarme', fallback: 'Alarme ligado à central + câmera frontal com gravação (obrigatoriedade do loteamento)' },
      { label: 'Condomínio',    ns: 'condominium', key: 'gatehouse',   fallback: 'Portaria 24 h do Ninho Verde I · muros H máx 1,80 m' },
    ],
  },
];

/** Resolve a SpecRowDef into its final {label, value} pair. */
function resolveRow(row: SpecRowDef, specs: ProjectSpecs): SpecRow {
  if ('compose' in row) {
    return { label: row.label, value: row.compose(specs) };
  }
  return { label: row.label, value: specValue(specs, row.ns, row.key, row.fallback) };
}

export function Specs({
  data,
  specs = {},
}: {
  data: PublicShowcase;
  specs?: ProjectSpecs;
}) {
  // Resolve every row at render time. Missing keys fall back to the
  // curated text — the page never goes blank because of a missing DB
  // entry. JP edits values in /vitrine/<id> Specs tab and they reflect
  // here on next page load (5-minute edge cache).
  const groups = SPEC_GROUPS.map((g) => ({
    group: g.group,
    rows: g.rows.map((r) => resolveRow(r, specs)),
  }));
  const headline: Array<{ label: string; value: string }> = [];
  if (data.areaSqm != null)
    headline.push({ label: 'Área construída', value: `${data.areaSqm} m²` });
  if (data.lotSqm != null)
    headline.push({ label: 'Terreno', value: `${data.lotSqm} m²` });
  if (data.bedrooms != null)
    headline.push({ label: 'Dormitórios', value: String(data.bedrooms) });
  if (data.bathrooms != null)
    headline.push({ label: 'Banheiros', value: String(data.bathrooms) });
  if (data.parkingSpots != null)
    headline.push({ label: 'Vagas', value: String(data.parkingSpots) });

  return (
    <section className="relative border-t border-line bg-bg py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-6 sm:px-12 lg:px-20">
        <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-accent">
          Ficha técnica
        </p>
        <h2 className="mt-5 max-w-[16ch] font-display text-4xl font-light leading-[1.05] tracking-tighter text-ink sm:text-5xl">
          Os números que importam.
        </h2>

        {headline.length > 0 && (
          <dl className="mt-16 grid grid-cols-2 gap-x-10 gap-y-10 sm:grid-cols-3 md:grid-cols-5">
            {headline.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-10%' }}
                transition={{
                  duration: 0.8,
                  delay: i * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <dt className="text-[10px] font-medium uppercase tracking-[0.2em] text-ink-subtle">
                  {s.label}
                </dt>
                <dd className="mt-3 font-display text-3xl font-light tracking-tight text-ink tabular-nums">
                  {s.value}
                </dd>
              </motion.div>
            ))}
          </dl>
        )}

        {/* Extended specs — arranged as 5 groups with 2-col key-value
            lists. Reveals in a single fade-up when the whole block
            enters the viewport (not per-row, to avoid visual noise). */}
        <motion.div
          className="mt-20 grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-x-14 md:gap-y-14"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10%' }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        >
          {groups.map((grp) => (
            <div key={grp.group}>
              <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-accent">
                {grp.group}
              </p>
              <dl className="mt-5 divide-y divide-line/40">
                {grp.rows.map((r) => (
                  <div key={r.label} className="flex flex-col gap-1 py-3 sm:flex-row sm:justify-between sm:gap-6">
                    <dt className="text-[11px] font-medium uppercase tracking-[0.15em] text-ink-subtle shrink-0 sm:w-40">
                      {r.label}
                    </dt>
                    <dd className="text-sm font-serif leading-relaxed text-ink sm:text-right [text-wrap:pretty]">
                      {r.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </motion.div>

        {data.amenities.length > 0 && (
          <div className="mt-20">
            <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-ink-subtle">
              Também conta com
            </p>
            <ul className="mt-5 flex flex-wrap gap-x-8 gap-y-3 text-lg text-ink sm:text-xl">
              {data.amenities.map((a) => (
                <li key={a} className="font-display font-light">
                  {a}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
